import Amplify, { Hub } from "@aws-amplify/core";
import {
  DefaultTheme,
  NavigationContainer,
  NavigationState,
} from "@react-navigation/native";
import {
  Provider as PaperProvider,
  DefaultTheme as PaperDefaultTheme,
} from "react-native-paper";
import { createStackNavigator } from "@react-navigation/stack";
import * as Font from "expo-font";
import mixpanel, { Config as MixpanelConfig } from "mixpanel-browser";
import React, { lazy, Suspense } from "react";
import Modal from "react-modal";
import { View } from "react-native";
import { Text, ThemeProvider } from "react-native-elements";
import {
  atom,
  RecoilRoot,
  useRecoilValue,
  useRecoilValueLoadable,
} from "recoil";
// @ts-ignore Ignore missing types
import awsconfig from "./aws-exports";
import * as mixpanelConfig from "./mixpanel.json";
import { selfGuidedModeState } from "./src/client";
import Header from "./src/components/Header";
import { Loading } from "./src/components/Loading";
import ScreenLayout from "./src/components/ScreenLayout";
import { WithAuth } from "./src/components/WithAuth";
import { getActiveRoute } from "./src/navigation";
import { onboardingProfileState } from "./src/onboarding";
import { createScreenLayoutNavigator } from "./src/ScreenLayoutNavigator";
import { colors, fontFamilies, fonts, theme } from "./src/styles";
import { Persona, RfpType } from "./src/types";

const LoadingWrapper: (Screen: React.FC, hideHeader?: boolean) => React.FC = (
  Screen,
  hideHeader
) => {
  return (props: any) => {
    const onboarded = useRecoilValueLoadable(onboardingProfileState);

    if (
      onboarded.state !== "hasValue" ||
      onboarded.contents.doneOnboarding === false
    ) {
      // Use the outermost / initial loading screen while the initial onboarding check
      // is happeing
      return <Screen {...props} />;
    }

    // If the type persona of the user is know, and onboarding is done, then
    // a loading screen with the right header and right nav bar can be shown
    return (
      <Suspense
        fallback={
          <LoadingScreen
            {...props}
            persona={onboarded.contents.profile.persona}
            hideHeader={hideHeader}
          />
        }
      >
        <Screen {...props} />
      </Suspense>
    );
  };
};

const lazyWrappedScreen = (
  LazyScreen: React.LazyExoticComponent<React.ComponentType<any>>,
  hideHeader?: boolean
) => {
  return LoadingWrapper(LazyScreen, hideHeader);
};

const BidScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/BidScreen"))
);
const MetrologistHomeScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/MetrologistHomeScreen"))
);
const NewBidScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/NewBidScreen"))
);
const NewPropertyScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/NewPropertyScreen")),
  true
);
const NewRFPScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/NewRFPScreen")),
  true
);
const PersonaScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/PersonaScreen"))
);
const SubcontractorProfileScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/ProfileScreen"))
);
const MetrologistProfileScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/ProfileScreen"))
);
const PropertyManagerProfileScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/ProfileScreen")),
  true
);
const PropertyManagerHomeScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/PropertyManagerHomeScreen")),
  true
);

const PropertyManagerCompanyScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/CompanyScreen")),
  true
);

const PropertyManagerProvidersScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/ProvidersScreen")),
  true
);

const PropertyManagerPropertiesScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/PropertyManagerPropertiesScreen")),
  true
);

const PropertyManagerTrendsScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/MarketTrendsScreen")),
  true
);

const PropertyManagerProposalsScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/ProposalsScreen")),
  true
);

const PropertyScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/PropertyScreen")),
  true
);
const RFPMeasurementScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/RFPMeasurementScreen"))
);
const RFPScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/RFPScreen")),
  true
);
const PhotoScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/PhotoScreen"))
);
const SubcontractorHomeScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/SubcontractorHomeScreen"))
);
const BulkUploadScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/BulkUploadScreen")),
  true
);

const OnboardingScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/OnboardingScreen"))
);

const SelfGuidedTourScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/SelfGuidedTourScreen")),
  true
);

const SelfGuidedModalScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/SelfGuidedModalScreen")),
  true
);

const StripeTestScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/StripeTestScreen"))
);
const ColorsScreen = lazyWrappedScreen(
  lazy(() => import("./src/screens/ColorsScreen")),
  true
);
const Credits = lazyWrappedScreen(lazy(() => import("./src/screens/Credits")));
const DataAgreement = lazyWrappedScreen(
  lazy(() => import("./src/screens/DataAgreementScreen"))
);
const Storybook = lazyWrappedScreen(lazy(() => import("./storybook")));

const MockPropertiesGenerator = lazyWrappedScreen(
  lazy(() => import("./src/screens/MockPropertiesGeneratorScreen")),
  true
);

mixpanel.init(mixpanelConfig.token, {
  ignore_dnt: true,
} as Partial<MixpanelConfig>);

Hub.listen(/.*/, (data) => {
  if (data.channel === "auth") {
    if (data.payload.event !== "configured") {
      mixpanel.track(`Amplify Auth - ${data.payload.event}`, {
        message: data.payload.message,
      });
    }
  } else if (data.channel === "UI Auth") {
    mixpanel.track(`Amplify UI Auth - ${data.payload.message}`);
  }
});

// const FAKE_TOKEN =
//   "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI3ZDhjYTUyOC00OTMxLTQyNTQtOTI3My1lYTVlZTg1M2YyNzEiLCJjb2duaXRvOmdyb3VwcyI6W10sImVtYWlsX3ZlcmlmaWVkIjp0cnVlLCJhbGdvcml0aG0iOiJIUzI1NiIsImlzcyI6Imh0dHBzOi8vY29nbml0by1pZHAudXMtZWFzdC0xLmFtYXpvbmF3cy5jb20vdXMtZWFzdC0xX2Zha2VfaWRwIiwicGhvbmVfbnVtYmVyX3ZlcmlmaWVkIjp0cnVlLCJjb2duaXRvOnVzZXJuYW1lIjoibWRsYXZpbiIsImNvZ25pdG86cm9sZXMiOltdLCJhdWQiOiIyaGlmYTA5NmIzYTI0bXZtM3Boc2t1YXFpMyIsImV2ZW50X2lkIjoiMThmNDA2N2UtOTk4NS00ZWFlLTlmMzMtZjQ1ZjQ5NTQ3MGQwIiwidG9rZW5fdXNlIjoiaWQiLCJwaG9uZV9udW1iZXIiOiIrMTIwNjIwNjIwMTYiLCJleHAiOjE1NzQ5NDgzOTYsImVtYWlsIjoibWF0dC5sYXZpbkBnbWFpbC5jb20iLCJhdXRoX3RpbWUiOjE1NzQ5MDUxOTYsImlhdCI6MTU3NDkwNTE5Nn0.gybxEYA1yWTbn8r-Z0laftk5oIxVARX2LoP1FctEM68";

const LoadingScreen: React.FC<{
  persona: Persona | null;
  hideHeader?: boolean;
}> = (props) => {
  if (props.hideHeader) return null;

  if (props.persona === null) {
    return (
      <View>
        <Header />
        <Text>Loading page ...</Text>
      </View>
    );
  } else {
    return (
      <ScreenLayout persona={props.persona}>
        <Text>Loading page ...</Text>
      </ScreenLayout>
    );
  }
};

export type OpenStackParamList = {
  Credits: undefined;
  Storybook: undefined;
  DataAgreement: undefined;
};

export type PropertyManagerParamList = {
  PropertyManagerHome: undefined;
  PropertyManagerCompany: undefined;
  PropertyManagerProviders: undefined;
  PropertyManagerTrends: undefined;
  PropertyManagerProposals: undefined;
  PropertyManagerProperties: undefined;
  PropertyManagerProfile: undefined;
  NewProperty: undefined;
  NewRFP: { propertyId: string; type?: RfpType };
  Property: { propertyId: string };
  RFP: { rfpId: string };
  BulkUpload:
    | { step: "review" | "create" | "spreadsheet" | "importReport" }
    | undefined;
};

export type ServiceProviderParamList = {
  Bid: { bidId: string; checkout?: string };
  SubcontractorHome: undefined;
  SubcontractorProfile: undefined;
  NewBid: { bidRequestId: string };
  Photo: {
    identityId: string;
    path: string;
  };
  StripeTest: undefined;
};

export type AuthedStackParamList = {
  Persona: undefined;
  Onboarding: undefined;
  SelfGuidedTour: undefined;
  PropertyManager: undefined;
  ServiceProvider: undefined;
  Metroloist: undefined;
};

export type MetrologistStackParamList = {
  MetrologistProfile: undefined;
  MetrologistHome: undefined;
  RFPMeasurementScreen: { rfpId: string };
};

export type RootStackParamList = OpenStackParamList &
  AuthedStackParamList &
  PropertyManagerParamList &
  ServiceProviderParamList &
  MetrologistStackParamList;

const propertyManagerScreenLinks: Record<
  keyof PropertyManagerParamList,
  string
> = {
  PropertyManagerHome: "manager",
  PropertyManagerCompany: "manager/company",
  PropertyManagerProperties: "manager/properties",
  PropertyManagerProviders: "manager/providers",
  PropertyManagerTrends: "manager/trends",
  PropertyManagerProposals: "manager/proposals",
  PropertyManagerProfile: "manager/profile",
  Property: "property/:propertyId",
  RFP: "rfp/:rfpId",
  NewProperty: "newProperty",
  NewRFP: "property/:propertyId/newRFP",
  BulkUpload: "bulkUpload/:step?",
};

const serviceProviderScreenLinks: Record<
  keyof ServiceProviderParamList,
  string
> = {
  SubcontractorHome: "provider",
  SubcontractorProfile: "provider/profile",
  NewBid: "provider/bidRequest/:bidRequestId/newBid",
  Bid: "bid/:bidId",
  Photo: "photo/:identityId/:path",
  StripeTest: "stripeTest",
};

const metroloistScreenLinks: Record<keyof MetrologistStackParamList, string> = {
  MetrologistProfile: "metrologist/profile",
  MetrologistHome: "metrologist",
  RFPMeasurementScreen: "metrologist/rfp/:rfpId/measure",
};

const authedScreenLinks: Record<
  keyof AuthedStackParamList,
  string | { screens: { [key: string]: string } }
> = {
  Persona: "",
  Onboarding: "onboarding",
  SelfGuidedTour: "tour",
  PropertyManager: {
    screens: propertyManagerScreenLinks,
  },
  ServiceProvider: {
    screens: serviceProviderScreenLinks,
  },
  Metroloist: {
    screens: metroloistScreenLinks,
  },
};

export const linking = {
  prefixes: [],
  config: {
    screens: {
      Authed: {
        screens: {
          ...authedScreenLinks,
        },
      },
      Open: {
        screens: {
          Credits: "credits",
          DataAgreement: "data-agreement",
          Storybook: "storybook",
          SelfGuidedModal: "selfGuidedModal",
          MockPropertiesGenerator: "mockPropertiesGenerator",
          Colors: "colors",
        },
      },
    },
  },
};

const Stack = createStackNavigator();

const HEADER_SCREEN_OPTIONS = {
  header: () => <Header />,
  animationEnabled: false,
};

export const OpenNavigator: React.FC = () => (
  <Stack.Navigator screenOptions={HEADER_SCREEN_OPTIONS}>
    <Stack.Screen name="Credits" component={Credits} />
    <Stack.Screen name="DataAgreement" component={DataAgreement} />
    <Stack.Screen name="Storybook" component={Storybook} />
    <Stack.Screen name="SelfGuidedModal" component={SelfGuidedModalScreen} />
    <Stack.Screen
      name="MockPropertiesGenerator"
      component={MockPropertiesGenerator}
    />
    <Stack.Screen name="Colors" component={ColorsScreen} />
  </Stack.Navigator>
);

export const defaultRoutes = {
  PropertyManager: "PropertyManagerHome" as const,
  ServiceProvider: "SubcontractorHome" as const,
  Metrologist: "MetrologistProfile" as const,
};

const { Navigator: PropertyManagerNavigator, Screen: PropertyManagerScreen } =
  createScreenLayoutNavigator<PropertyManagerParamList>();

const PropertyManagerScreens: React.FC = () => {
  return (
    <PropertyManagerNavigator
      persona={Persona.PropertyManager}
      initialRouteName={defaultRoutes["PropertyManager"]}
    >
      <PropertyManagerScreen
        name="PropertyManagerCompany"
        component={PropertyManagerCompanyScreen}
      />
      <PropertyManagerScreen
        name="PropertyManagerHome"
        component={PropertyManagerHomeScreen}
      />

      <PropertyManagerScreen
        name="PropertyManagerProviders"
        component={PropertyManagerProvidersScreen}
      />

      <PropertyManagerScreen
        name="PropertyManagerTrends"
        component={PropertyManagerTrendsScreen}
      />

      <PropertyManagerScreen
        name="PropertyManagerProposals"
        component={PropertyManagerProposalsScreen}
      />

      <PropertyManagerScreen
        name="PropertyManagerProperties"
        component={PropertyManagerPropertiesScreen}
      />
      <PropertyManagerScreen
        name="PropertyManagerProfile"
        component={PropertyManagerProfileScreen}
      />
      <PropertyManagerScreen name="Property" component={PropertyScreen} />
      <PropertyManagerScreen name="RFP" component={RFPScreen} />
      <PropertyManagerScreen name="NewProperty" component={NewPropertyScreen} />
      <PropertyManagerScreen name="NewRFP" component={NewRFPScreen} />
      <PropertyManagerScreen name="BulkUpload" component={BulkUploadScreen} />
    </PropertyManagerNavigator>
  );
};

const { Navigator: ServiceProviderNavigator, Screen: ServiceProviderScreen } =
  createScreenLayoutNavigator<ServiceProviderParamList>();

const ServiceProviderScreens: React.FC = () => (
  <ServiceProviderNavigator
    persona={Persona.ServiceProvider}
    initialRouteName={defaultRoutes["ServiceProvider"]}
  >
    <ServiceProviderScreen
      name="SubcontractorHome"
      component={SubcontractorHomeScreen}
    />

    <ServiceProviderScreen
      name="SubcontractorProfile"
      component={SubcontractorProfileScreen}
    />
    <ServiceProviderScreen name="NewBid" component={NewBidScreen} />
    <ServiceProviderScreen name="Bid" component={BidScreen} />
    <ServiceProviderScreen name="Photo" component={PhotoScreen} />
    <ServiceProviderScreen name="StripeTest" component={StripeTestScreen} />
  </ServiceProviderNavigator>
);

const { Navigator: MetrologistNavigator, Screen: MetrologistScreen } =
  createScreenLayoutNavigator<MetrologistStackParamList>();

const MetrologistScreens: React.FC = () => (
  <MetrologistNavigator
    persona={Persona.Metrologist}
    initialRouteName={defaultRoutes["Metrologist"]}
  >
    <MetrologistScreen
      name="MetrologistProfile"
      component={MetrologistProfileScreen}
    />
    <MetrologistScreen
      name="MetrologistHome"
      component={MetrologistHomeScreen}
    />
    <MetrologistScreen
      name="RFPMeasurementScreen"
      component={RFPMeasurementScreen}
    />
  </MetrologistNavigator>
);

const AuthedNavigator: React.FC = () => (
  <Stack.Navigator screenOptions={NO_HEADER_SCREEN_OPTIONS}>
    <Stack.Screen name="Onboarding" component={OnboardingScreen} />
    <Stack.Screen name="SelfGuidedTour" component={SelfGuidedTourScreen} />
    <Stack.Screen name="PropertyManager" component={PropertyManagerScreens} />
    <Stack.Screen name="ServiceProvider" component={ServiceProviderScreens} />
    <Stack.Screen name="Metrologist" component={MetrologistScreens} />
    <Stack.Screen name="Persona" component={PersonaScreen} />
  </Stack.Navigator>
);

const NO_HEADER_SCREEN_OPTIONS = {
  header: () => null,
  animationEnabled: false,
};

const AppNavigator: React.FC = () => (
  <Stack.Navigator screenOptions={NO_HEADER_SCREEN_OPTIONS}>
    <Stack.Screen name="Authed" component={AuthedNavigator} />
    <Stack.Screen name="Open" component={OpenNavigator} />
  </Stack.Navigator>
);

awsconfig.oauth.redirectSignIn = window.location.origin + "/";
awsconfig.oauth.redirectSignOut = window.location.origin + "/";

// Use a nicer auth domain in production
if (awsconfig.aws_user_pools_id === "us-east-1_EDUvbEPgF") {
  awsconfig.oauth.domain = "auth.app.bomdata.io";
}

Amplify.configure(awsconfig);

const buildNavigationStateChangeHandler =
  (selfGuidedTour: boolean) => (state: NavigationState | undefined) => {
    if (!state) return;
    const currentRoute = getActiveRoute(state);

    if (currentRoute) {
      mixpanel.track(`Show Screen - ${currentRoute.name}`, {
        // The parameters passed to the mixpanel.track API might be modified
        // so make a copy to ensure that the navigation state is not changed
        ...currentRoute.params,
        ...(selfGuidedTour ? { selfGuidedTour } : {}),
      });
    }
  };

const NavigationTheme = {
  ...DefaultTheme,
  colors: {
    ...DefaultTheme.colors,
    background: "white",
  },
};

Modal.setAppElement("#root");

const fontsState = atom({
  key: "Fonts",
  default: Font.loadAsync({
    [fontFamilies["Poppins Bold"]]: require("./assets/fonts/Poppins-Bold.ttf"),
    [fontFamilies["Poppins"]]: require("./assets/fonts/Poppins-Regular.ttf"),
    // Needed for the icon on the add property button
    anticon: require("./node_modules/react-native-vector-icons/Fonts/AntDesign.ttf"),
    // Needed for various icons
    feather: require("./node_modules/@expo/vector-icons/build/vendor/react-native-vector-icons/Fonts/Feather.ttf"),
  }),
});

const FontLoader: React.FC = () => {
  useRecoilValue(fontsState);
  return null;
};

const Contents: React.FC = () => {
  const selfGuidedTour = useRecoilValue(selfGuidedModeState);
  const onNavigationStateChange =
    buildNavigationStateChangeHandler(selfGuidedTour);

  return (
    <>
      <FontLoader />
      <NavigationContainer
        linking={linking}
        theme={NavigationTheme}
        onStateChange={onNavigationStateChange}
      >
        <AppNavigator />
      </NavigationContainer>
    </>
  );
};

const paperTheme: typeof PaperDefaultTheme = {
  ...PaperDefaultTheme,
  fonts: {
    regular: {
      fontFamily: fonts.bodyText,
    },
    medium: {
      fontFamily: fonts.bodyText,
    },
    light: {
      fontFamily: fonts.bodyText,
    },
    thin: {
      fontFamily: fonts.bodyText,
    },
  },
  dark: false,
  roundness: 0,
  colors: {
    ...PaperDefaultTheme.colors,
    primary: colors.primary,
    text: colors.neutral900,
    surface: colors.neutral050,
  },
};

export default function AuthentiatedApp() {
  React.useEffect(() => {
    mixpanel.track("AppLoaded");
  }, []);

  return (
    <RecoilRoot>
      <ThemeProvider theme={theme}>
        <PaperProvider theme={paperTheme}>
          <WithAuth>
            <Suspense fallback={<Loading />}>
              <Contents />
            </Suspense>
          </WithAuth>
        </PaperProvider>
      </ThemeProvider>
    </RecoilRoot>
  );
}
